import { useEffect, useState } from "react";
import Sidebar from "../../../shared/components/Sidebar";
import PageLayout from "../../../shared/components/PageLayout";
import "./biodata.css";
import BackButton from "../../../shared/components/Backbtn";
import { useNavigate } from "react-router-dom";
import { Button, CircularProgress, Snackbar, FormControl, InputLabel, Select, MenuItem, Alert } from "@mui/material";
import useBiodataViewModel from "../hooks/useBiodataViewModel";
import { getDefaultImage } from "../../../shared/utils/imageHelpers";
import { useFirebase } from "../../../context/Firebase";

const Biodata = () => {
  const navigate = useNavigate();
  const { getUserFromFirestore } = useFirebase();
  const [filteredProfiles, setFilteredProfiles] = useState([]);
  const [creatorNames, setCreatorNames] = useState({});
  const [selectedImages, setSelectedImages] = useState([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [filterStatus, setFilterStatus] = useState("");
  const [imageErrorMap, setImageErrorMap] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const profilesPerPage = 20;

  const {
    profiles,
    loading,
    error,
    handleDelete: vmHandleDelete, // Renamed to avoid conflict
    handleStatusChange: vmHandleStatusChange, // Renamed to avoid conflict
  } = useBiodataViewModel();

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");

  useEffect(() => {
    if (error) {
      setSnackbarMessage(error);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  }, [error]);

  // Update filtered profiles when profiles from ViewModel change or filter status changes
  useEffect(() => {
    if (filterStatus === "") {
      setFilteredProfiles(profiles);
    }
    else {
      const filtered = profiles.filter((profile) => profile.status === filterStatus);
      setFilteredProfiles(filtered);
    }
    // Reset to first page when filter changes
    setCurrentPage(1);
  }, [profiles, filterStatus]);

  // Resolve createdBy UIDs to user names
  useEffect(() => {
    const resolveNames = async () => {
      const uidRegex = /^[a-zA-Z0-9]{20,}$/;
      const uidsToResolve = profiles
        .map((p) => p.createdBy)
        .filter((val) => val && uidRegex.test(val) && !creatorNames[val]);
      const unique = [...new Set(uidsToResolve)];
      if (unique.length === 0) return;

      const resolved = { ...creatorNames };
      for (const uid of unique) {
        try {
          const userDoc = await getUserFromFirestore(uid);
          if (userDoc.exists()) {
            resolved[uid] = userDoc.data().name || userDoc.data().email || uid;
          }
        } catch {
          // keep uid as-is
        }
      }
      setCreatorNames(resolved);
    };
    resolveNames();
  }, [profiles]); // eslint-disable-line react-hooks/exhaustive-deps

  // Calculate pagination
  const indexOfLastProfile = currentPage * profilesPerPage;
  const indexOfFirstProfile = indexOfLastProfile - profilesPerPage;
  const currentProfiles = filteredProfiles.slice(indexOfFirstProfile, indexOfLastProfile);
  const totalPages = Math.ceil(filteredProfiles.length / profilesPerPage);

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // eslint-disable-next-line no-unused-vars
  const handleDelete = async (id) => {
    const success = await vmHandleDelete(id);
    if (success) {
      setSnackbarMessage("Profile deleted successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } else {
      // Error message is already set by ViewModel
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleStatusChange = async (id, newStatus) => {
    const success = await vmHandleStatusChange(id, newStatus);
    if (success) {
      setSnackbarMessage("Profile status updated successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } else {
      // Error message is already set by ViewModel
    }
  };

  const handleViewProfile = (profileId) => {
    navigate(`/biodata/${profileId}`);
  };

  const handleImageClick = (profile) => {
    const imgs = profile.imagePaths?.length > 0
      ? profile.imagePaths
      : [getDefaultImage(profile.gender)];
    setSelectedImages(imgs);
    setSelectedImageIndex(0);
  };

  const handleCloseImagePopup = () => {
    setSelectedImageIndex(null);
  };

  const handlePrevImage = (e) => {
    e.stopPropagation();
    setSelectedImageIndex((prev) => (prev > 0 ? prev - 1 : selectedImages.length - 1));
  };

  const handleNextImage = (e) => {
    e.stopPropagation();
    setSelectedImageIndex((prev) => (prev < selectedImages.length - 1 ? prev + 1 : 0));
  };

  const formatValue = (key, value) => {
  if (value instanceof Date) {
    return value.toLocaleDateString();
  }
  if (Array.isArray(value)) {
    return value.length > 0 ? value.join(", ") : "N/A";
  }
  if (typeof value === "object" && value !== null) {
    return JSON.stringify(value); // fallback to prevent crash
  }
  return value ?? "N/A";
};

  const handleFilterChange = (e) => {
    setFilterStatus(e.target.value);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  if (loading && profiles.length === 0) {
    return (
      <div className="dashboard">
        <Sidebar />
        <div className="dashboard--content">
          <CircularProgress />
        </div>
      </div>
    );
  }

  return (
  <>
    <div className="dashboard">
      <Sidebar />
      <div className="dashboard--content">
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
        <PageLayout
          title="Marriage Profiles"
          actions={
            <>
              <BackButton tooltip="Back" />
              <Button variant="contained" color="primary" onClick={() => navigate("/add-biodata")}>
                Create Profile
              </Button>
            </>
          }
        >
          <div className="filter-container">
          <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="status-filter-label">Filter by Status</InputLabel>
            <Select
              labelId="status-filter-label"
              id="status-filter"
              value={filterStatus}
              onChange={handleFilterChange}
              label="Filter by Status"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Created">Created</MenuItem>
              <MenuItem value="Reported">Reported</MenuItem>
              <MenuItem value="Accepted">Accepted</MenuItem>
              <MenuItem value="Hide">Hide</MenuItem>
              <MenuItem value="Show">Show</MenuItem>
            </Select>
          </FormControl>
        </div>

        <div className="post-list">
          {currentProfiles.length === 0 && !loading ? (
            <p className="no-posts">No profiles found.</p>
          ) : (
            currentProfiles.map((profile) => (
              <div key={profile.id} className="profile-card-horizontal" onClick={() => handleViewProfile(profile.id)}>
                <div className="profile-card-image" onClick={(e) => { e.stopPropagation(); if (!imageErrorMap[profile.id]) handleImageClick(profile); }}>
                  <img
                    src={
                      imageErrorMap[profile.id]
                        ? getDefaultImage(profile.gender)
                        : profile.imagePaths?.[0] || getDefaultImage(profile.gender)
                    }
                    onError={() => setImageErrorMap((prev) => ({ ...prev, [profile.id]: true }))}
                    alt={profile.fullName || "Profile"}
                  />
                </div>
                <div className="profile-card-info">
                  <h3 className="profile-card-name">{profile.fullName || "N/A"}</h3>
                  <div className="profile-card-grid">
                    <div className="profile-card-field"><span className="field-label">Gender</span><span>{formatValue("gender", profile.gender)}</span></div>
                    <div className="profile-card-field"><span className="field-label">Age</span><span>{formatValue("ageRange", profile.ageRange)}</span></div>
                    <div className="profile-card-field"><span className="field-label">Caste</span><span>{formatValue("caste", profile.caste)}</span></div>
                    <div className="profile-card-field"><span className="field-label">Height</span><span>{formatValue("height", profile.height)}</span></div>
                    <div className="profile-card-field"><span className="field-label">Qualification</span><span>{formatValue("qualification", profile.qualification)}</span></div>
                    <div className="profile-card-field"><span className="field-label">Marital Status</span><span>{formatValue("maritalStatus", profile.maritalStatus)}</span></div>
                    <div className="profile-card-field"><span className="field-label">Income</span><span>{formatValue("income", profile.income)}</span></div>
                    <div className="profile-card-field"><span className="field-label">Workplace</span><span>{formatValue("workplace", profile.workplace)}</span></div>
                  </div>
                  <div className="profile-card-status">
                    <span className={`status-badge status-${(profile.status || "created").toLowerCase()}`}>
                      {profile.status || "Created"}
                    </span>
                    {profile.createdBy && <span className="profile-card-created-by">Added by: {creatorNames[profile.createdBy] || profile.createdBy}</span>}
                  </div>
                </div>
                <div className="profile-card-arrow">
                  ›
                </div>
              </div>
            ))
          )}
        </div>

        {filteredProfiles.length > 0 && (
          <div className="pagination-container">
            <div className="pagination-info">
              Showing {indexOfFirstProfile + 1} to {Math.min(indexOfLastProfile, filteredProfiles.length)} of {filteredProfiles.length} profiles
            </div>
            <div className="pagination-controls">
              <Button
                variant="outlined"
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                sx={{ mr: 1 }}
              >
                Previous
              </Button>

              <div className="pagination-numbers">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNumber) => {
                  // Show first page, last page, current page, and pages around current
                  if (
                    pageNumber === 1 ||
                    pageNumber === totalPages ||
                    (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                  ) {
                    return (
                      <Button
                        key={pageNumber}
                        variant={currentPage === pageNumber ? "contained" : "outlined"}
                        onClick={() => handlePageClick(pageNumber)}
                        sx={{ minWidth: '40px', mx: 0.5 }}
                      >
                        {pageNumber}
                      </Button>
                    );
                  } else if (
                    pageNumber === currentPage - 2 ||
                    pageNumber === currentPage + 2
                  ) {
                    return <span key={pageNumber} style={{ margin: '0 5px' }}>...</span>;
                  }
                  return null;
                })}
              </div>

              <Button
                variant="outlined"
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                sx={{ ml: 1 }}
              >
                Next
              </Button>
            </div>
          </div>
        )}

        </PageLayout>
      </div>
    </div>

    {selectedImageIndex !== null && (
      <div className="biodata-lightbox" onClick={handleCloseImagePopup}>
        <button className="biodata-lightbox-close" onClick={handleCloseImagePopup}>✕</button>
        {selectedImages.length > 1 && (
          <button className="biodata-lightbox-nav biodata-lightbox-prev" onClick={handlePrevImage}>‹</button>
        )}
        <div className="biodata-lightbox-img-wrap" onClick={(e) => e.stopPropagation()}>
          <img src={selectedImages[selectedImageIndex]} alt="Profile" />
        </div>
        {selectedImages.length > 1 && (
          <button className="biodata-lightbox-nav biodata-lightbox-next" onClick={handleNextImage}>›</button>
        )}
        {selectedImages.length > 1 && (
          <div className="biodata-lightbox-counter">{selectedImageIndex + 1} / {selectedImages.length}</div>
        )}
      </div>
    )}
  </>
  );
};

export default Biodata;
